import React, { useCallback, useState } from "react";
import { Accept, DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import { Close, InsertDriveFileOutlined, FileUploadOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, styled, Typography as T } from "@mui/material";
import prettyBytes from "pretty-bytes";

interface FileDropzoneProps<FileState> {
  files: FileState;
  setFiles: (files: any) => any;
  dropzoneConfig?: DropzoneOptions;
}

const listFormatter = new Intl.ListFormat("en");

const formatAcceptedFileTypes = (accept: Accept): string => {
  // Combine list of accepted types
  const combinedList = Object.values(accept).flatMap((values) => values);
  // Get rid of potential duplicates
  const acceptedTypes = Array.from(new Set(combinedList));
  // Format list and remove prepended dot from extensions
  return listFormatter.format(acceptedTypes).replaceAll(".", "");
};

const VisuallyHiddenInput = styled("input")({
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FileDropzone = <FileState extends File[] | (File | undefined)>({
  files,
  setFiles,
  dropzoneConfig = {},
}: FileDropzoneProps<FileState>) => {
  const [dragErrorMessage, setDragErrorMessage] = useState("");
  const { accept, maxSize, maxFiles } = dropzoneConfig;
  const multi = maxFiles !== 1;
  const showRestrictions = Boolean(accept || maxSize);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setDragErrorMessage("");
      if (!multi) {
        setFiles(acceptedFiles[0] as FileState);
      } else {
        setFiles([...(files as File[]), ...acceptedFiles] as FileState);
      }

      if (fileRejections.length) {
        const errorMessage =
          fileRejections[0].errors[0]?.message || "File rejected for unknown reasons";
        if (errorMessage.includes("File type must be")) {
          setDragErrorMessage("Please upload an accepted file type.");
        } else {
          setDragErrorMessage(errorMessage);
        }
      }
    },
    [multi, setFiles, files]
  );

  const removeFile = (index: number) => {
    const currentFiles = files as File[];
    const updatedFiles = [...currentFiles.slice(0, index), ...currentFiles.slice(index + 1)];
    setFiles(updatedFiles);
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    ...dropzoneConfig,
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const showSelectedFile = !multi && files;

  return (
    <Stack gap={2}>
      <Stack
        {...getRootProps()}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          gap: 0.5,
          border: 1,
          borderStyle: "dashed",
          borderColor: "grey.400",
          bgcolor: "grey.100",
          padding: 3,
          textAlign: "center",
          borderRadius: 1.5,
          svg: {
            fontSize: 50,
          },
        }}
      >
        {!showSelectedFile ? (
          <FileUploadOutlined />
        ) : (
          <Box>
            <InsertDriveFileOutlined />
            <T variant="body2">{(files as File)?.name}</T>
          </Box>
        )}
        <T variant="body1" sx={{ fontWeight: 500, opacity: 0.7, mb: 1.5 }}>
          {dragErrorMessage || "Browse or drop files here to upload"}
        </T>
        <Button variant="contained" onClick={open}>
          Browse Files...
          <VisuallyHiddenInput {...getInputProps()} />
        </Button>
      </Stack>
      {showRestrictions && (
        <Box>
          {accept && (
            <T
              variant="caption"
              display="block"
              align="center"
              fontSize="0.875rem"
              color="grey.800"
            >
              Accepted file types: {formatAcceptedFileTypes(accept)}
            </T>
          )}
          {maxSize !== undefined && (
            <T
              variant="caption"
              display="block"
              align="center"
              fontSize="0.875rem"
              color="grey.800"
            >
              (maximum {prettyBytes(maxSize)})
            </T>
          )}
        </Box>
      )}
      {/* Show rows for multiple files */}
      {multi && Array.isArray(files) && files.length > 0 && (
        <Stack
          gap={0.5}
          minHeight={50}
          // Magic max-height number based on minimum height of dialog excluding this area (so title, sibling content, actions)
          // TODO: Look for a better way to handle this inner overflow/scroll
          maxHeight="calc(100vh - 485px)"
          overflow="auto"
        >
          {(files as File[]).map(({ name, size }, index) => (
            <Box
              key={`${name}-${index}`}
              display="flex"
              gap={1}
              p={1}
              borderRadius={0.75}
              bgcolor="grey.100"
              alignItems="center"
            >
              <InsertDriveFileOutlined fontSize="small" />
              {name}
              <Box flex={1} />
              <T variant="subtitle2" sx={{ fontWeight: 400, opacity: 0.7, whiteSpace: "nowrap" }}>
                {prettyBytes(size)}
              </T>
              <IconButton size="small" onClick={() => removeFile(index)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export const SingleFileDropzone = (props: FileDropzoneProps<File | undefined>) => (
  <FileDropzone<File | undefined>
    {...props}
    dropzoneConfig={{ ...(props.dropzoneConfig ?? {}), maxFiles: 1 }}
  />
);
export const MultiFileDropzone = FileDropzone<File[]>;
